import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import ExportButtons from "@/components/ExportButtons";
import { exportToExcel, exportToPDF } from "@/lib/export";

const EXPORT_COLS = [
  { header: "Tambak", accessor: (r) => r.tambak_nama },
  { header: "Kode", accessor: (r) => r.tambak_kode },
  { header: "Blok", accessor: (r) => r.blok },
  { header: "Luas (m2)", accessor: (r) => r.luas_m2 },
  { header: "Siklus", accessor: (r) => r.siklus_count },
  { header: "Hari Monitoring", accessor: (r) => r.hari_monitoring },
  { header: "Total Pakan (kg)", accessor: (r) => r.total_pakan_kg },
  { header: "Total Panen (kg)", accessor: (r) => r.total_panen_kg },
  { header: "Total Nilai (Rp)", accessor: (r) => r.total_panen_nilai },
  { header: "FCR", accessor: (r) => r.fcr ?? "-" },
  { header: "Produktivitas (kg/ha)", accessor: (r) => r.produktivitas ?? "-" },
];

export default function AnalyticsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/tambak-comparison").then(({ data: d }) => {
      setData(d.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const formatCurrency = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);

  const hasHarvest = data.filter((d) => d.total_panen_kg > 0);
  const chartData = data.filter((d) => d.siklus_count > 0).slice(0, 15);

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Memuat data analytics...</div>;

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Analytics Tambak</h1>
          <p className="text-sm text-slate-500 mt-1">Perbandingan performa antar-tambak</p>
        </div>
        <ExportButtons onExcel={() => exportToExcel(data, EXPORT_COLS, "analytics_tambak")} onPDF={() => exportToPDF(data, EXPORT_COLS, "analytics_tambak", "Analytics Perbandingan Tambak P3UW")} />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total Tambak</p>
          <p className="text-2xl font-semibold text-[#1A1A1A] mt-1" style={{ fontFamily: "Work Sans" }}>{data.length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Punya Siklus</p>
          <p className="text-2xl font-semibold text-[#008080] mt-1" style={{ fontFamily: "Work Sans" }}>{data.filter((d) => d.siklus_count > 0).length}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total Panen</p>
          <p className="text-2xl font-semibold text-[#B8860B] mt-1" style={{ fontFamily: "Work Sans" }}>{data.reduce((a, b) => a + (b.total_panen_kg || 0), 0).toLocaleString("id-ID")} kg</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Rata-rata FCR</p>
          <p className="text-2xl font-semibold text-[#1A1A1A] mt-1" style={{ fontFamily: "Work Sans" }}>
            {hasHarvest.length > 0 ? (hasHarvest.reduce((a, b) => a + (b.fcr || 0), 0) / hasHarvest.length).toFixed(2) : "-"}
          </p>
        </CardContent></Card>
      </div>

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Pakan vs Panen (kg)</CardTitle></CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="tambak_kode" tick={{ fontSize: 9, fill: "#64748B" }} angle={-45} textAnchor="end" />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total_pakan_kg" name="Pakan (kg)" fill="#B8860B" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="total_panen_kg" name="Panen (kg)" fill="#008080" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Nilai Penjualan per Tambak</CardTitle></CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="tambak_kode" tick={{ fontSize: 9, fill: "#64748B" }} angle={-45} textAnchor="end" />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(0)}jt` : v >= 1000 ? `${(v/1000).toFixed(0)}rb` : v} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="total_panen_nilai" name="Nilai (Rp)" fill="#008080" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comparison Table */}
      <Card className="bg-white border border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Perbandingan Detail</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider font-bold sticky left-0 bg-slate-50 z-10">Tambak</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Blok</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Siklus</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Hari Mon.</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Pakan (kg)</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Panen (kg)</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Nilai</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">FCR</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Prod. (kg/ha)</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">pH</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Suhu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">Belum ada data</TableCell></TableRow>
                ) : data.map((r) => (
                  <TableRow key={r.tambak_id} className="hover:bg-slate-50">
                    <TableCell className="text-xs font-medium sticky left-0 bg-white z-10">{r.tambak_kode} - {r.tambak_nama}</TableCell>
                    <TableCell className="text-xs">{r.blok}</TableCell>
                    <TableCell className="text-xs text-center">{r.siklus_count}</TableCell>
                    <TableCell className="text-xs text-center">{r.hari_monitoring}</TableCell>
                    <TableCell className="text-xs text-right font-medium">{r.total_pakan_kg > 0 ? r.total_pakan_kg.toLocaleString("id-ID") : "-"}</TableCell>
                    <TableCell className="text-xs text-right font-semibold text-[#008080]">{r.total_panen_kg > 0 ? r.total_panen_kg.toLocaleString("id-ID") : "-"}</TableCell>
                    <TableCell className="text-xs text-right font-medium text-[#B8860B]">{r.total_panen_nilai > 0 ? formatCurrency(r.total_panen_nilai) : "-"}</TableCell>
                    <TableCell className="text-xs text-center">
                      {r.fcr ? <Badge className={`${r.fcr < 1.5 ? "bg-emerald-100 text-emerald-700" : r.fcr < 2 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} hover:bg-inherit text-[10px]`}>{r.fcr}</Badge> : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-center font-medium">{r.produktivitas ?? "-"}</TableCell>
                    <TableCell className="text-xs text-center">{r.avg_ph ?? "-"}</TableCell>
                    <TableCell className="text-xs text-center">{r.avg_suhu ?? "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
